'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Download, Info } from 'lucide-react'

import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@ui/web/components'
import { HELP_URL } from '@helper/utils/core'
import { formatCurrency } from '@helper/utils/format'

import { useSharedTRPC } from '../../context/trpc'
import { RoyaltyDetailsTable } from './table'

export interface RoyaltyDetailsPageProps {
  royaltyId: string
  /** Link or button to go back (e.g. royalty list). Rendered as a child of the back control. */
  backSlot?: React.ReactNode
  /** Optional URL for the back link when using default back UI. */
  backHref?: string
  className?: string
}

export function RoyaltyDetailsPage({ royaltyId, backSlot, backHref, className }: RoyaltyDetailsPageProps) {
  const trpc = useSharedTRPC()
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(10)

  const { data, isPending, isError, error, refetch } = useQuery({
    ...trpc.royalty.royaltyDetails.queryOptions({ royaltyId, page, pageSize }),
    staleTime: 1000 * 60 * 5,
  })

  const handleDownload = React.useCallback(() => {
    if (!data?.downloadUrl) return
    const a = document.createElement('a')
    a.href = data.downloadUrl
    a.download = `royalty-report.csv`
    a.rel = 'noopener noreferrer'
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [data?.downloadUrl, data?.periodLabel])

  if (isPending) {
    return (
      <div className={className}>
        <div className='srd:flex srd:flex-col srd:gap-6'>
          {/* Back */}
          <div>{backSlot ?? (backHref != null ? <Skeleton className='srd:h-5 srd:w-16' /> : null)}</div>

          <div className='srd:flex srd:flex-col srd:gap-2'>
            {/* Partner name */}
            <Skeleton className='srd:inline-block srd:h-4 srd:w-48' />

            {/* Title */}
            <Skeleton className='srd:h-7 srd:w-64' />
          </div>

          {/* Summary cards */}
          <div className='srd:grid srd:gap-4 srd:sm:grid-cols-2'>
            <Card className='srd:shadow-none'>
              <CardHeader className='srd:pb-2'>
                <CardTitle className='srd:text-xs srd:font-medium'>Total Earned Royalty</CardTitle>
              </CardHeader>
              <CardContent className='srd:space-y-1'>
                <Skeleton className='srd:h-6 srd:w-32' />
                <Skeleton className='srd:mt-2 srd:h-3 srd:w-40' />
              </CardContent>
            </Card>
            <Card className='srd:shadow-none'>
              <CardHeader className='srd:pb-2'>
                <CardTitle className='srd:sr-only'>Summary</CardTitle>
              </CardHeader>
              <CardContent className='srd:space-y-1 srd:text-xs'>
                <div className='srd:flex srd:items-center srd:gap-2 srd:flex-wrap'>
                  <p>Total unique sessions:</p> <Skeleton className='srd:inline-block srd:ml-1 srd:h-4 srd:w-8' />
                </div>
                <div className='srd:flex srd:items-center srd:gap-2 srd:flex-wrap'>
                  <p>Shared amount:</p> <Skeleton className='srd:inline-block srd:ml-1 srd:h-4 srd:w-24' />
                </div>
                <Skeleton className='srd:mt-2 srd:h-4 srd:w-20' />
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          <RoyaltyDetailsTable
            data={[]}
            isLoading
            downloadButton={<Skeleton className='srd:h-10 srd:w-28 srd:rounded-full' />}
          />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={className}>
        <p className='srd:text-main-red-100'>Gagal memuat detail royalty: {error.message}</p>
        <Button onClick={async () => await refetch()} variant='outline' size='sm' className='srd:mt-4 srd:w-fit'>
          Coba lagi
        </Button>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className='srd:flex srd:flex-col srd:gap-6'>
        {/* Back */}
        <div>
          <Button variant='ghost' className='srd:m-0 srd:p-0 srd:text-base' asChild>
            {backSlot ??
              (backHref != null ? (
                <a href={backHref}>
                  <ArrowLeft className='srd:h-4 srd:w-4' />
                  Back
                </a>
              ) : null)}
          </Button>
        </div>

        <div className='srd:flex srd:flex-col srd:gap-2'>
          {/* Partner name */}
          <p className='srd:text-neutrals-n-75'>
            <span className='srd:font-bold'>{data.organizationName}</span>{' '}
            {data.organizationEntityName ? `(${data.organizationEntityName})` : ''}
          </p>

          {/* Title */}
          <h1 className='srd:text-complementary-black srd:text-xl srd:font-bold'>{data.periodLabel}</h1>
        </div>

        {/* Summary cards */}
        <div className='srd:grid srd:gap-4 srd:sm:grid-cols-2'>
          <Card className='srd:shadow-none'>
            <CardHeader className='srd:pb-2'>
              <CardTitle className='srd:text-xs srd:font-medium'>Total Earned Royalty</CardTitle>
            </CardHeader>
            <CardContent className='srd:space-y-1'>
              <p className='srd:text-complementary-black srd:font-semibold'>
                {formatCurrency(data.totalAmount, { currency: data.currency, maximumFractionDigits: 2 })}
              </p>

              <p className='srd:text-neutrals-n-75 srd:text-xs srd:mt-2'>
                You earned {data.partnerPercentage ?? 0}% of shared amount
              </p>
            </CardContent>
          </Card>
          <Card className='srd:shadow-none'>
            <CardHeader className='srd:pb-2'>
              <CardTitle className='srd:sr-only'>Summary</CardTitle>
            </CardHeader>
            <CardContent className='srd:space-y-1 srd:text-xs'>
              <p>
                Total unique sessions: <span className='srd:font-medium'>{data.totalUniqueSession}</span>
              </p>

              <p>Shared amount: {formatCurrency(data.sharedAmount ?? 0, { currency: data.currency })}</p>

              <Button variant='ghost' asChild className='srd:p-0 srd:text-xs srd:mt-2'>
                <a href={HELP_URL} target='_blank'>
                  <Info className='srd:size-4 srd:mr-2' />
                  Learn more
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <RoyaltyDetailsTable
          data={data.data}
          pagination={data.pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          downloadButton={
            <Button
              variant='outline'
              size='default'
              onClick={handleDownload}
              disabled={!data.downloadUrl}
              className='srd:border-main-blue-75 srd:text-main-blue-100 srd:rounded-full'
            >
              <Download className='srd:mr-2 srd:h-4 srd:w-4' />
              Download
            </Button>
          }
        />
      </div>
    </div>
  )
}
